/**
 * AdminDashboard.jsx — DeutschBridge Institute
 * Updated: Added "My Classes" section at the top showing teacher cohorts.
 */
import React, { useState, useEffect } from 'react';
import {
  apiGetAllStudents, apiGetAdminStats,
  apiGetStudentReport, apiUnlockLevel, apiMarkLevelComplete,
} from '../../utils/api';
import { useToast } from '../../context/ToastContext';
import {
  Card, StatCard, Button, Badge, LevelBadge, PaymentBadge,
  Modal, Spinner, SectionHeader, AIThinking,
} from '../../components/shared/UI';
import API from '../../utils/apiClient';

const CEFR_LEVELS = ['A1','A2','B1','B2'];

function timeAgo(d) {
  if (!d) return '—';
  const days = Math.floor((Date.now() - new Date(d).getTime()) / 86400000);
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  return `${days} days ago`;
}

const LEVEL_COLORS = {
  A1: { bg:'bg-gold-50',   border:'border-gold-300',   text:'text-yellow-800', badge:'bg-gold-400 text-de-black' },
  A2: { bg:'bg-brand-50',  border:'border-brand-200',  text:'text-brand-700',  badge:'bg-brand-500 text-white'   },
  B1: { bg:'bg-blue-50',   border:'border-blue-200',   text:'text-blue-800',   badge:'bg-blue-600 text-white'    },
  B2: { bg:'bg-purple-50', border:'border-purple-200', text:'text-purple-800', badge:'bg-purple-600 text-white'  },
};

// ── My Classes Section ────────────────────────────────────────
function MyClasses() {
  const { addToast } = useToast();
  const [cohorts, setCohorts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    API.get('/cohorts/teacher')
      .then(({ data }) => setCohorts(data.cohorts || []))
      .catch(() => addToast('Failed to load your cohorts', 'error'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
        <div className="flex items-center gap-3 mb-4">
          <h2 className="text-base font-bold text-de-black">My Classes</h2>
        </div>
        <div className="flex justify-center py-8"><Spinner className="text-brand-500" /></div>
      </div>
    );
  }

  if (cohorts.length === 0) {
    return (
      <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center text-base"
            style={{ background:'#1A1A1A' }}>
            <span>🏫</span>
          </div>
          <h2 className="text-base font-bold text-de-black">My Classes</h2>
        </div>
        <div className="text-center py-8 border-2 border-dashed border-gray-200 rounded-xl">
          <p className="text-3xl mb-2">📋</p>
          <p className="text-sm font-bold text-de-black mb-1">No cohorts assigned yet</p>
          <p className="text-xs text-gray-500">
            Contact your Super Admin to be assigned as a teacher to a cohort.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
      {/* Header */}
      <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between"
        style={{ background:'#1A1A1A' }}>
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-brand-500 flex items-center justify-center text-base">
            🏫
          </div>
          <div>
            <h2 className="text-sm font-bold text-white">My Classes</h2>
            <p className="text-xs text-gray-400">
              {cohorts.length} cohort{cohorts.length !== 1 ? 's' : ''} assigned to you
            </p>
          </div>
        </div>
        {/* German flag stripe */}
        <div className="flex gap-0.5">
          <div className="w-2 h-6 rounded-sm bg-de-black" />
          <div className="w-2 h-6 rounded-sm bg-brand-500" />
          <div className="w-2 h-6 rounded-sm bg-gold-400" />
        </div>
      </div>

      {/* Cohort cards */}
      <div className="p-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {cohorts.map(cohort => {
          const lc      = LEVEL_COLORS[cohort.level] || LEVEL_COLORS.A1;
          const isFull  = cohort.status === 'full' || cohort.spotsLeft === 0;
          const hasNext = cohort.nextClass;

          return (
            <div key={cohort.id}
              className={`rounded-xl border-2 overflow-hidden transition-shadow hover:shadow-md ${lc.border}`}>

              {/* Card top bar */}
              <div className={`px-4 py-3 flex items-center justify-between ${lc.bg}`}>
                <div className="flex items-center gap-2">
                  {/* Level badge */}
                  <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${lc.badge}`}>
                    {cohort.level}
                  </span>
                  <span className={`text-xs font-medium capitalize ${lc.text}`}>
                    {cohort.session}
                  </span>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded-full font-semibold
                  ${cohort.status === 'open'   ? 'bg-green-100 text-green-700'  :
                    cohort.status === 'full'   ? 'bg-yellow-100 text-yellow-700':
                    'bg-gray-100 text-gray-500'}`}>
                  {isFull ? 'Full' : 'Open'}
                </span>
              </div>

              {/* Main content */}
              <div className="p-4">
                <h3 className="font-bold text-de-black text-sm mb-3 leading-tight">
                  {cohort.name}
                </h3>

                {/* Class details */}
                <div className="space-y-1.5 mb-4">
                  <div className="flex items-center gap-2 text-xs text-gray-600">
                    <span className="text-gray-400 w-4 text-center">📅</span>
                    <span className="font-medium">{cohort.daysAbbr || '—'}</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-600">
                    <span className="text-gray-400 w-4 text-center">⏰</span>
                    <span className="font-medium">
                      {cohort.startTimeFmt || cohort.start_time}
                      {cohort.endTimeFmt ? ` — ${cohort.endTimeFmt}` : ''}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-600">
                    <span className="text-gray-400 w-4 text-center">👥</span>
                    <span>
                      <span className="font-bold text-de-black">{cohort.current_students}</span>
                      <span className="text-gray-400">/{cohort.max_students} students</span>
                    </span>
                  </div>
                </div>

                {/* Enrollment bar */}
                <div className="mb-4">
                  <div className="w-full bg-gray-100 rounded-full h-1.5">
                    <div className="h-1.5 rounded-full bg-brand-500 transition-all"
                      style={{
                        width: `${Math.min(100, ((cohort.current_students || 0) / (cohort.max_students || 20)) * 100)}%`
                      }} />
                  </div>
                  <p className="text-xs text-gray-400 mt-1">
                    {cohort.spotsLeft} spot{cohort.spotsLeft !== 1 ? 's' : ''} remaining
                  </p>
                </div>

                {/* Next class highlight */}
                {hasNext && (
                  <div className="mb-3 px-3 py-2 rounded-lg border border-gold-300 bg-gold-50">
                    <p className="text-xs font-bold text-yellow-800">Next class</p>
                    <p className="text-xs text-yellow-700">
                      {cohort.nextClass.display} at {cohort.startTimeFmt || cohort.start_time}
                    </p>
                  </div>
                )}

                {/* Join button */}
                {cohort.meet_link ? (
                  <a
                    href={cohort.meet_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl font-bold text-sm text-white transition-opacity hover:opacity-90"
                    style={{ background: '#DD0000' }}>
                    🔗 Join Class
                  </a>
                ) : (
                  <div className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl text-sm text-gray-400 bg-gray-100 border-2 border-dashed border-gray-200">
                    No Meet link yet
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Main AdminDashboard ────────────────────────────────────────
export default function AdminDashboard() {
  const { addToast } = useToast();
  const [students, setStudents]   = useState([]);
  const [stats, setStats]         = useState(null);
  const [loading, setLoading]     = useState(true);
  const [managingStudent, setManagingStudent] = useState(null);
  const [reportStudent, setReportStudent]     = useState(null);
  const [reportData, setReportData]           = useState(null);
  const [reportLoading, setReportLoading]     = useState(false);
  const [actionLoading, setActionLoading]     = useState('');

  useEffect(() => {
    Promise.all([apiGetAllStudents(), apiGetAdminStats()])
      .then(([{ students: s }, statsData]) => { setStudents(s); setStats(statsData); })
      .catch(() => addToast('Failed to load dashboard data', 'error'))
      .finally(() => setLoading(false));
  }, []);

  const openReport = async (student) => {
    setReportStudent(student);
    setReportData(null);
    setReportLoading(true);
    try {
      const data = await apiGetStudentReport(student.id);
      setReportData(data);
    } catch (err) { addToast(err.message || 'Failed to generate report', 'error'); }
    finally { setReportLoading(false); }
  };

  const handleUnlock = async (studentId, level) => {
    setActionLoading(`unlock_${studentId}_${level}`);
    try {
      const { unlockedLevels, currentLevel } = await apiUnlockLevel(studentId, level);
      setStudents(prev => prev.map(s => s.id === studentId ? { ...s, unlockedLevels, currentLevel } : s));
      if (managingStudent?.id === studentId) setManagingStudent(prev => ({ ...prev, unlockedLevels, currentLevel }));
      addToast(`Level ${level} unlocked!`, 'success');
    } catch (err) { addToast(err.message || 'Failed', 'error'); }
    finally { setActionLoading(''); }
  };

  const handleMarkComplete = async (studentId, level) => {
    setActionLoading(`complete_${studentId}_${level}`);
    try {
      const { completedLevels } = await apiMarkLevelComplete(studentId, level);
      setStudents(prev => prev.map(s => s.id === studentId ? { ...s, completedLevels } : s));
      if (managingStudent?.id === studentId) setManagingStudent(prev => ({ ...prev, completedLevels }));
      addToast(`Level ${level} marked complete!`, 'success');
    } catch (err) { addToast(err.message || 'Failed', 'error'); }
    finally { setActionLoading(''); }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">

      {/* ── MY CLASSES (at top) ──────────────────────────────── */}
      <MyClasses />

      {/* Stats row */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <StatCard label="Total Students"  value={stats?.totalStudents  ?? '—'} icon="👥" color="black" />
        <StatCard label="Exams This Week" value={stats?.examsThisWeek  ?? '—'} icon="📝" color="red" />
        <StatCard label="Average Score"   value={stats?.avgScore ? `${stats.avgScore}%` : '—'} icon="📊" color="green" />
        <StatCard label="Needs Attention" value={stats?.atRisk         ?? '—'} icon="⚠️" color="red" />
        <StatCard label="Pending Unlocks" value={stats?.pendingPayments ?? '—'} icon="🔓" color="gold" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Student table */}
        <div className="lg:col-span-2">
          <Card className="p-6" accent="red">
            <SectionHeader title="My Students" subtitle={`${students.length} assigned to you`} />
            {loading ? (
              <div className="flex justify-center py-12"><Spinner size="lg" className="text-brand-500" /></div>
            ) : students.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-4xl mb-3">👥</p>
                <p className="text-sm font-bold text-de-black mb-1">No students assigned yet</p>
                <p className="text-xs text-gray-500">Ask your Super Admin to assign students to you.</p>
              </div>
            ) : (
              <div className="overflow-x-auto -mx-2">
                <table className="w-full text-sm min-w-[700px]">
                  <thead>
                    <tr className="border-b-2 border-gray-100">
                      {['Student','Level','Last Active','Avg Score','Payment','Actions'].map(h => (
                        <th key={h} className="text-left py-3 px-3 text-xs font-bold text-gray-500 uppercase tracking-wide">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {students.map(s => (
                      <tr key={s.id} className="border-b border-gray-50 hover:bg-surface transition-colors">
                        <td className="py-3 px-3">
                          <div className="flex items-center gap-2">
                            <div className="w-7 h-7 rounded-full bg-brand-500 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">
                              {s.name?.charAt(0) || '?'}
                            </div>
                            <div>
                              <p className="font-semibold text-de-black text-xs">{s.name}</p>
                              <p className="text-xs text-gray-400">{s.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-3">
                          <span className="font-bold text-brand-500">{s.currentLevel}</span>
                        </td>
                        <td className="py-3 px-3 text-xs text-gray-500 whitespace-nowrap">
                          {timeAgo(s.lastActive)}
                        </td>
                        <td className="py-3 px-3">
                          <span className={`font-bold text-sm
                            ${s.avgScore >= 75 ? 'text-green-600' :
                              s.avgScore >= 60 ? 'text-yellow-600' : 'text-brand-500'}`}>
                            {s.avgScore ? `${s.avgScore}%` : '—'}
                          </span>
                        </td>
                        <td className="py-3 px-3">
                          <PaymentBadge status={s.paymentStatus || 'pending'} />
                        </td>
                        <td className="py-3 px-3">
                          <div className="flex gap-1.5">
                            <Button size="sm" variant="secondary" onClick={() => openReport(s)}>Report</Button>
                            <Button size="sm" variant="primary"   onClick={() => setManagingStudent(s)}>Levels</Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </div>

        {/* Quick stats sidebar */}
        <div>
          <Card className="p-5" accent="gold">
            <SectionHeader title="Quick Stats" />
            {stats ? (
              <div className="space-y-3">
                {[
                  { label:'Total Revenue',    value:`GHS ${Number(stats.totalRevenue||0).toLocaleString()}`, bg:'bg-green-50', text:'text-green-700' },
                  { label:'Exams this week',  value: stats.examsThisWeek,   bg:'bg-surface',  text:'text-de-black'   },
                  { label:'Pending payments', value: stats.pendingPayments, bg:'bg-gold-50',  text:'text-yellow-700' },
                  { label:'At-risk students', value: stats.atRisk,          bg:'bg-brand-50', text:'text-brand-500'  },
                ].map(item => (
                  <div key={item.label} className={`flex justify-between items-center p-3 ${item.bg} rounded-xl`}>
                    <span className="text-sm text-gray-600">{item.label}</span>
                    <span className={`font-bold ${item.text}`}>{item.value ?? '—'}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex justify-center py-6"><Spinner className="text-brand-500" /></div>
            )}
          </Card>
        </div>
      </div>

      {/* ── Manage Levels Modal ──────────────────────────────── */}
      <Modal isOpen={!!managingStudent} onClose={() => setManagingStudent(null)}
        title={`Manage Levels — ${managingStudent?.name}`} size="md">
        {managingStudent && (
          <div className="space-y-3">
            {CEFR_LEVELS.map(level => {
              const isUnlocked  = managingStudent.unlockedLevels?.includes(level);
              const isCompleted = managingStudent.completedLevels?.includes(level);
              const isActive    = managingStudent.currentLevel === level;
              const status      = isCompleted ? 'completed' : isUnlocked ? 'active' : 'locked';
              return (
                <div key={level} className={`p-4 rounded-xl border-2 flex items-center justify-between gap-3
                  ${isCompleted ? 'border-green-200 bg-green-50' : isActive ? 'border-gold-400 bg-gold-50' : 'border-gray-200'}`}>
                  <div className="flex items-center gap-3">
                    <LevelBadge level={level} status={status} />
                    <div>
                      <p className="text-sm font-semibold text-de-black">Level {level}</p>
                      <p className="text-xs text-gray-500 capitalize">{status}</p>
                    </div>
                  </div>
                  <div className="flex gap-2 flex-wrap justify-end">
                    {isUnlocked && !isCompleted && (
                      <Button size="sm" variant="success"
                        loading={actionLoading === `complete_${managingStudent.id}_${level}`}
                        onClick={() => handleMarkComplete(managingStudent.id, level)}>
                        ✓ Mark Complete
                      </Button>
                    )}
                    {!isUnlocked && (
                      <Button size="sm" variant="primary"
                        loading={actionLoading === `unlock_${managingStudent.id}_${level}`}
                        onClick={() => handleUnlock(managingStudent.id, level)}>
                        🔓 Unlock
                      </Button>
                    )}
                    {isCompleted && <Badge variant="completed">✅ Done</Badge>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Modal>

      {/* ── AI Student Report Modal ──────────────────────────── */}
      <Modal isOpen={!!reportStudent} onClose={() => { setReportStudent(null); setReportData(null); }}
        title={`AI Report — ${reportStudent?.name}`} size="lg">
        {reportLoading ? (
          <AIThinking message="Claude AI is generating a detailed report…" />
        ) : reportData ? (
          <div className="space-y-5">
            <div className="grid grid-cols-3 gap-3">
              <div className="p-3 bg-brand-50 rounded-xl text-center">
                <p className="text-xl font-bold text-brand-500">{reportStudent?.avgScore ?? '—'}%</p>
                <p className="text-xs text-gray-500">Avg Score</p>
              </div>
              <div className="p-3 bg-green-50 rounded-xl text-center">
                <p className="text-xl font-bold text-green-600">{reportStudent?.completedLevels?.length ?? 0}</p>
                <p className="text-xs text-gray-500">Levels Done</p>
              </div>
              <div className="p-3 bg-gold-50 rounded-xl text-center">
                <p className="text-xl font-bold text-yellow-700">{reportStudent?.unlockedLevels?.length ?? 0}</p>
                <p className="text-xs text-gray-500">Unlocked</p>
              </div>
            </div>
            {reportData.report && (
              <>
                <div className="p-4 bg-surface rounded-xl border border-gray-200">
                  <p className="text-xs font-bold text-de-black mb-2 flex items-center gap-1">
                    <span className="w-5 h-5 rounded bg-brand-500 text-white inline-flex items-center justify-center text-xs">🤖</span>
                    AI Summary
                  </p>
                  <p className="text-sm text-de-text">{reportData.report.summary}</p>
                </div>
                {reportData.report.strengths?.length > 0 && (
                  <div>
                    <h4 className="text-sm font-bold text-de-black mb-2">✅ Strengths</h4>
                    <ul className="space-y-1">
                      {reportData.report.strengths.map((s, i) => (
                        <li key={i} className="text-xs text-de-text flex gap-2">
                          <span className="text-green-500">•</span>{s}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {reportData.report.weaknesses?.length > 0 && (
                  <div>
                    <h4 className="text-sm font-bold text-de-black mb-2">⚠️ Weak Areas</h4>
                    <div className="flex flex-wrap gap-2">
                      {reportData.report.weaknesses.map((w, i) => (
                        <Badge key={i} variant="red" size="sm">{w}</Badge>
                      ))}
                    </div>
                  </div>
                )}
                {reportData.report.recommendedActions?.length > 0 && (
                  <div>
                    <h4 className="text-sm font-bold text-de-black mb-2">📌 Recommended Actions</h4>
                    <div className="space-y-1.5">
                      {reportData.report.recommendedActions.map((a, i) => (
                        <div key={i} className="flex items-start gap-2 text-xs text-de-text">
                          <span className="text-brand-500 font-bold flex-shrink-0">{i + 1}.</span>{a}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div className="flex gap-3 p-3 bg-surface rounded-xl">
                  <div className="flex-1">
                    <p className="text-xs text-gray-500">Exam Readiness</p>
                    <p className="text-sm text-de-text mt-0.5">{reportData.report.examReadiness}</p>
                  </div>
                  {reportData.report.predictedExamScore && (
                    <div className="text-right">
                      <p className="text-xs text-gray-500">Predicted Score</p>
                      <p className="text-xl font-bold text-brand-500">{reportData.report.predictedExamScore}%</p>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        ) : null}
      </Modal>
    </div>
  );
}
